import { usePlayerSheet } from '@/app/contexts/PlayerSheetContext';
import { useAuth } from '@/hooks/useAuth';
import { useGuildData } from '@/hooks/useGuildData';
import { cn } from '@/utils/cn';
import { getProfileInGameName } from '@/utils/profile';
import { formatAppRoleLabel, getPrimaryRole } from '@/utils/roles';
import { RoleEmblem } from '@/components/shared/RoleEmblem';

interface PlayerNameButtonProps {
  profileId?: string;
  primaryLabel?: string;
  secondaryLabel?: string;
  className?: string;
  compact?: boolean;
  showBadgeCount?: boolean;
}

export function PlayerNameButton({
  profileId,
  primaryLabel,
  secondaryLabel,
  className,
  compact = false,
  showBadgeCount = !compact,
}: PlayerNameButtonProps): JSX.Element {
  const { isApproved, isSignedIn, needsOnboarding } = useAuth();
  const { state } = useGuildData();
  const { openPlayerSheet } = usePlayerSheet();
  const linkedProfile = profileId ? state.profiles.find((entry) => entry.id === profileId) : null;
  const primaryRole = linkedProfile ? getPrimaryRole(linkedProfile.roles) : 'member';
  const primaryText = primaryLabel ?? getProfileInGameName(linkedProfile);
  const secondaryText = secondaryLabel ?? linkedProfile?.displayName;
  const metaParts = Array.from(
    new Set(
      [secondaryText, linkedProfile && isApproved && !needsOnboarding ? formatAppRoleLabel(primaryRole) : undefined]
        .map((value) => value?.trim())
        .filter((value): value is string => Boolean(value && value !== primaryText)),
    ),
  );

  if (!profileId || !linkedProfile || !isSignedIn || !isApproved || needsOnboarding) {
    return (
      <span className={cn('player-name-button player-name-button--static', compact && 'player-name-button--compact', className)}>
        {linkedProfile ? <RoleEmblem role={primaryRole} compact /> : null}
        <span className="player-name-button__text">
          <strong className="player-name-button__primary">{primaryText}</strong>
          {metaParts.length > 0 ? <span className="player-name-button__meta">{metaParts.join(' | ')}</span> : null}
        </span>
      </span>
    );
  }

  return (
    <button
      type="button"
      className={cn('player-name-button', compact && 'player-name-button--compact', className)}
      onClick={() => openPlayerSheet(profileId)}
    >
      <RoleEmblem role={primaryRole} compact />
      <span className="player-name-button__text">
        <strong className="player-name-button__primary">{primaryText}</strong>
        {metaParts.length > 0 ? <span className="player-name-button__meta">{metaParts.join(' | ')}</span> : null}
      </span>
      {showBadgeCount && linkedProfile.badges.length > 0 ? (
        <span className="player-name-button__badge-count">{linkedProfile.badges.length}</span>
      ) : null}
    </button>
  );
}
